import { Label, LabelAssignment } from '@provision/entity-types';
import { useCallback, useMemo, useState } from 'react';
import { uniqueBy } from 'remeda';
import { DynamicIcon, Tooltip } from 'src/components';
import { GroupedSelectMenu } from 'src/components/SelectMenu';
import {
  GroupedSelectMenuOption,
  SelectMenuOption,
  SelectMenuTriggerState,
} from 'src/components/SelectMenu/select-menu.types';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import {
  LABEL_PICKER_PAGE_SIZE,
  onlyPublicsFilter,
  useGroupRules,
  useLabelAssignment,
  useLabelGroups,
  useLabels,
  UserGroupRules,
} from 'src/graphql';
import { ConditionInput } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { useDebouncedValue } from 'src/utils/hooks/use-debounced-value';

export interface LabelFormValues {
  label?: SelectMenuOption;
}

const PrivateIcon = () => (
  <Tooltip title="Private">
    <DynamicIcon
      name="visibility_off"
      fontSize="1rem"
      sx={{
        color: (theme) => theme.utils.important(theme.palette.custom.OV.sys.onSurfaceVariant),
      }}
    />
  </Tooltip>
);

const toLabelRow = (label: Label): SelectMenuOption => ({
  value: label.labelId,
  title: label.displayName ?? '',
  endIcon: label.isPublic === false ? <PrivateIcon /> : undefined,
});

export const LabelForm = ({
  labelAssignments,
  entityName,
  entityId,
  partnerId,
  renderTrigger,
}: {
  entityId: string;
  entityName: string;
  partnerId?: number;
  labelAssignments: LabelAssignment[];
  renderTrigger: (state: SelectMenuTriggerState) => React.ReactNode;
}) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showSuccessAlert } = useNotifications();
  const { createLabelAssignment, deleteLabelAssignment } = useLabelAssignment();
  const { groupRules } = useGroupRules();

  const canAccessToPrivateLabels = groupRules[UserGroupRules.AccessToPrivateLabels] as boolean;

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_DELAY);
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  const searching = Boolean(debouncedSearch);

  // Server-side filtering: private visibility is enforced in the query, not by
  // hiding rows, so a user without access never receives them.
  const filters = useMemo<ConditionInput[]>(() => {
    const next: ConditionInput[] = searching
      ? [{ attribute: 'displayName', operator: 'ilike', value: `%${debouncedSearch}%` }]
      : [];

    if (!canAccessToPrivateLabels) {
      next.push(onlyPublicsFilter);
    }

    return next;
  }, [searching, debouncedSearch, canAccessToPrivateLabels]);

  const queryPartnerId = selectedPartnerId ?? partnerId;

  // Root list: ungrouped labels when browsing; all matching labels when searching.
  // Groups stay discoverable from `labelGroups` and load their kids on open.
  const { labels, loading, hasMore, loadMore, isLoadingMore } = useLabels(
    {
      partnerId: queryPartnerId,
      includeOnlyOutsideGroups: !searching,
    },
    { filters, skip: !groupRules, limit: LABEL_PICKER_PAGE_SIZE },
  );

  const groupFilters = useMemo<ConditionInput[]>(
    () => (canAccessToPrivateLabels ? [] : [onlyPublicsFilter]),
    [canAccessToPrivateLabels],
  );

  const { labels: openGroupLabels, loading: openGroupLoading } = useLabels(
    { partnerId: queryPartnerId, groupId: openGroupId ?? undefined },
    { filters: groupFilters, skip: !groupRules || !openGroupId },
  );

  const { labelGroups } = useLabelGroups({ partnerId: queryPartnerId }, { skip: !groupRules });

  const labelsById = useMemo(() => {
    const assignedLabels = labelAssignments
      .map((assignment) => assignment.label)
      .filter((label): label is Label => Boolean(label));
    const merged = [...labels, ...openGroupLabels, ...assignedLabels];
    return new Map(
      uniqueBy(merged, (label) => label.labelId).map((label) => [label.labelId, label]),
    );
  }, [labelAssignments, labels, openGroupLabels]);

  const options = useMemo<GroupedSelectMenuOption[]>(() => {
    const groups = labelGroups.map((group) => {
      const groupId = group.labelGroupId || group.id;
      const groupIds = new Set(
        [group.id, group.labelGroupId].filter((id): id is string => Boolean(id)),
      );

      // Assignments seed children so group rows show checked / "N selected"
      // before the per-group fetch runs.
      const assignedChildren = uniqueBy(
        labelAssignments
          .map((assignment) => assignment.label)
          .filter((label): label is Label =>
            Boolean(label?.labelGroupId && groupIds.has(label.labelGroupId)),
          ),
        (label) => label.labelId,
      ).map(toLabelRow);

      let children: SelectMenuOption[];
      if (searching) {
        children = uniqueBy(
          labels.filter((label) => groupIds.has(label.labelGroupId)),
          (label) => label.labelId,
        ).map(toLabelRow);
      } else if (openGroupId && groupIds.has(openGroupId)) {
        const fetched = uniqueBy(
          openGroupLabels.filter((label) => groupIds.has(label.labelGroupId)),
          (label) => label.labelId,
        ).map(toLabelRow);
        children = uniqueBy([...fetched, ...assignedChildren], (row) => row.value);
      } else {
        // Keep the group row discoverable; remaining kids load on open.
        children = assignedChildren;
      }

      return {
        value: groupId,
        title: group.displayName ?? '',
        children,
      };
    });

    // While searching, hide groups with no matches. While browsing, keep every
    // group so deep catalogs stay discoverable before their page is loaded.
    const visibleGroups = searching
      ? groups.filter((group) => group.children.length > 0)
      : groups;

    const ungrouped = searching
      ? uniqueBy(
          labels.filter((label) => !label.labelGroupId),
          (label) => label.labelId,
        ).map(toLabelRow)
      : uniqueBy(labels, (label) => label.labelId).map(toLabelRow);

    return [...visibleGroups, ...ungrouped];
  }, [labelAssignments, labelGroups, labels, openGroupId, openGroupLabels, searching]);

  const values = useMemo(
    () => labelAssignments.map((assignment) => assignment.labelId),
    [labelAssignments],
  );

  const onGroupOpen = useCallback((group: GroupedSelectMenuOption) => {
    setOpenGroupId(group.value);
  }, []);

  const onOpenChange = useCallback((next: boolean) => {
    if (!next) {
      setOpenGroupId(null);
    }
  }, []);

  const onValuesChange = useCallback(
    async (_: string[], option: SelectMenuOption) => {
      const labelData = {
        entity: entityName,
        entityId,
        partnerId: queryPartnerId,
        canAccessToPrivateLabels,
      };

      const assigned = labelAssignments.some(
        (assignment) => assignment.labelId === option.value,
      );

      if (assigned) {
        const deleted = await deleteLabelAssignment({
          ...labelData,
          labelIds: option.value,
        });
        if (deleted?.length) {
          showSuccessAlert('Labels updated');
        }
        return;
      }

      const label = labelsById.get(option.value);
      const created = await createLabelAssignment({
        ...labelData,
        label: {
          ...label,
          displayName: option.title,
          labelId: option.value,
        } as Partial<Label>,
      });
      if (created?.length) {
        showSuccessAlert('Labels updated');
      }
    },
    [
      canAccessToPrivateLabels,
      createLabelAssignment,
      deleteLabelAssignment,
      entityId,
      entityName,
      labelAssignments,
      labelsById,
      queryPartnerId,
      showSuccessAlert,
    ],
  );

  return (
    <GroupedSelectMenu
      label="Labels"
      multiple
      options={options}
      values={values}
      onValuesChange={onValuesChange}
      onGroupOpen={onGroupOpen}
      onOpenChange={onOpenChange}
      search
      searchPlaceholder="Add labels…"
      searchValue={search}
      onSearchChange={setSearch}
      loading={loading || (Boolean(openGroupId) && openGroupLoading)}
      hasMore={hasMore}
      loadingMore={isLoadingMore}
      onLoadMore={loadMore}
      renderTrigger={renderTrigger}
    />
  );
};
